import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCloudUploadAlt,
  FaEye,
  FaEyeSlash,
  FaSignOutAlt
} from "react-icons/fa";
import config from "../config";
import {
  clearPreviewEditSession,
  clearPreviewMapDraft,
  compressImage,
  createEmptyPreviewMapForm,
  createPreviewMapFormFromCaregiver,
  formatPreviewRole,
  getPreviewProfileCompletion,
  getPreviewYouTubeEmbedUrl,
  getPreviewOwnerKey,
  isValidPreviewEmail,
  isValidPreviewYouTubeUrl,
  isConfirmedPreviewCountry,
  normalizePreviewMapFormFields,
  normalizePreviewEmail,
  MAX_PHOTO_SIZE,
  readFileAsDataUrl,
  readOwnedCaregiverTokens,
  readPreviewEditSession,
  readPreviewMapDraft,
  writeOwnedCaregiverTokens,
  writePreviewMapDraft
} from "../utils/previewMap";
import {
  formatPreviewPhoneNumber,
  getPreviewCountryPhoneConfig,
  getPreviewPhoneGuidance,
  isValidPreviewPhoneForCountry,
  PREVIEW_COUNTRY_GROUPS,
  sanitizePreviewNationalPhone
} from "../utils/previewCountryPhone";

const languageOptions = [
  "",
  "English",
  "Spanish",
  "French",
  "Swahili",
  "Arabic",
  "Portuguese",
  "Chinese",
  "Hindi",
  "German"
];

const genderOptions = [
  { value: "", label: "Select gender" },
  { value: "F", label: "Female" },
  { value: "M", label: "Male" }
];

const paymentOptions = [
  { value: "", label: "Select payment mode" },
  { value: "credit_card", label: "Credit Card" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" }
];

const statusOptions = [
  { value: "available", label: "Available" },
  { value: "unavailable", label: "Unavailable" }
];

const rateOptions = [1, 2, 3, 4, 5, 6];
const rateOptionValues = new Set(rateOptions.map(String));

const hasFormValue = (value) => String(value ?? "").trim().length > 0;

const createEmptyExperience = () => ({
  employer: "",
  role: "",
  location: "",
  period: "",
  notes: ""
});

const createDefaultForm = () => ({
  ...createEmptyPreviewMapForm(),
  experienceEntries: [createEmptyExperience()],
  latitude: "",
  longitude: ""
});

const createDraftKey = (isEditing, caregiverID) =>
  isEditing ? `edit:${caregiverID}` : "add:new";

const getDetailsPath = (isEditing, caregiverID) =>
  isEditing ? `/realMap/edit/${caregiverID}` : "/realMap/add";

const getLocationPath = (isEditing, caregiverID) =>
  `${getDetailsPath(isEditing, caregiverID)}/location`;

const PreviewMapForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { caregiverID = "" } = useParams();
  const lookupEmail = normalizePreviewEmail(searchParams.get("email"));
  const isEditing = Boolean(caregiverID);
  const isLocationStep = location.pathname.endsWith("/location");
  const withLookupEmail = useCallback(
    (path) =>
      !isEditing && lookupEmail ? `${path}?email=${encodeURIComponent(lookupEmail)}` : path,
    [isEditing, lookupEmail]
  );
  const detailsPath = withLookupEmail(getDetailsPath(isEditing, caregiverID));
  const locationPath = withLookupEmail(getLocationPath(isEditing, caregiverID));
  const draftKey = createDraftKey(isEditing, caregiverID);
  const editFlowPathFragment = `/realMap/edit/${caregiverID}`;

  const [form, setForm] = useState(createDefaultForm);
  const [loadingForm, setLoadingForm] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resolvingCountry, setResolvingCountry] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [error, setError] = useState("");
  const [hasPassword, setHasPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const feedbackRef = useRef(null);

  useEffect(() => {
    if (!error && !photoError) return;

    const frame = window.requestAnimationFrame(() => {
      feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [error, photoError]);

  const readActiveEditSession = useCallback(() => {
    if (!isEditing) return null;

    const activeSession = readPreviewEditSession();
    if (!activeSession?.token || activeSession.caregiverID !== caregiverID) {
      return null;
    }

    return activeSession;
  }, [caregiverID, isEditing]);

  const revokeEditSession = useCallback(async () => {
    if (!isEditing) return;

    const activeSession = readActiveEditSession();
    clearPreviewEditSession(caregiverID);

    if (!activeSession?.token) {
      return;
    }

    try {
      await axios.delete(`${config.API_BASE_URL}/caregiver-temp-map/edit-session`, {
        headers: {
          "x-preview-edit-session": activeSession.token
        }
      });
    } catch {
      // The local session has already been cleared. Backend expiry is a fallback.
    }
  }, [caregiverID, isEditing, readActiveEditSession]);

  const selectedCountryConfig = useMemo(
    () => getPreviewCountryPhoneConfig(form.country),
    [form.country]
  );

  const formattedPhone = useMemo(
    () => formatPreviewPhoneNumber(form.country, form.phone),
    [form.country, form.phone]
  );

  const profileCompletion = useMemo(
    () => getPreviewProfileCompletion(form),
    [form]
  );

  const videoPreviewUrl = useMemo(
    () => getPreviewYouTubeEmbedUrl(form.introVideoUrl),
    [form.introVideoUrl]
  );

  const hasRequiredDetails = Boolean(
    profileCompletion.profileComplete &&
      hasFormValue(form.role) &&
      isConfirmedPreviewCountry(form.country) &&
      selectedCountryConfig
  );

  const loadCountryView = useCallback(
    async (
      country,
      { preserveCoordinates = false, fallbackCoordinates = null } = {}
    ) => {
      const trimmedCountry = String(country || "").trim();

      if (!isConfirmedPreviewCountry(trimmedCountry)) {
        setForm((previous) => ({
          ...previous,
          latitude: "",
          longitude: ""
        }));
        return;
      }

      setResolvingCountry(true);
      setError("");

      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/caregiver-temp-map/country-center`,
          {
            params: { country: trimmedCountry }
          }
        );

        const latitude = Number(response.data.latitude);
        const longitude = Number(response.data.longitude);
        const fallbackLatitude = Number(fallbackCoordinates?.latitude);
        const fallbackLongitude = Number(fallbackCoordinates?.longitude);
        const nextCoordinates =
          preserveCoordinates &&
          Number.isFinite(fallbackLatitude) &&
          Number.isFinite(fallbackLongitude)
            ? [fallbackLatitude, fallbackLongitude]
            : [latitude, longitude];

        if (
          !preserveCoordinates ||
          nextCoordinates[0] !== fallbackLatitude ||
          nextCoordinates[1] !== fallbackLongitude
        ) {
          setForm((previous) => ({
            ...previous,
            latitude: nextCoordinates[0].toFixed(6),
            longitude: nextCoordinates[1].toFixed(6)
          }));
        }
      } catch (lookupError) {
        setForm((previous) => ({
          ...previous,
          latitude: "",
          longitude: ""
        }));
        setError(
          lookupError.response?.data?.error ||
            "Unable to place that country on the map."
        );
      } finally {
        setResolvingCountry(false);
      }
    },
    []
  );

  useEffect(() => {
    setError(location.state?.errorMessage || "");
  }, [location.state]);

  useEffect(() => {
    let ignore = false;

    const hydrateForm = async (nextForm) => {
      if (ignore) return;

      const normalizedForm = normalizePreviewMapFormFields(nextForm);

      setForm({
        ...normalizedForm,
        experienceEntries:
          Array.isArray(normalizedForm.experienceEntries) &&
          normalizedForm.experienceEntries.length
            ? normalizedForm.experienceEntries
            : [createEmptyExperience()]
      });

      if (isLocationStep && normalizedForm.country) {
        await loadCountryView(normalizedForm.country, {
          preserveCoordinates: true,
          fallbackCoordinates: normalizedForm
        });
      }
    };

    const loadInitialState = async () => {
      const savedDraft = readPreviewMapDraft();

      if (!isEditing && lookupEmail) {
        try {
          const response = await axios.get(
            `${config.API_BASE_URL}/caregiver-temp-map/imported-profile`,
            {
              params: { email: lookupEmail }
            }
          );
          const caregiver = response.data.caregiver;

          navigate(withLookupEmail(`/realMap/edit/${caregiver.caregiverID}`), {
            replace: true
          });
        } catch (lookupError) {
          if (!ignore) {
            setError(
              lookupError.response?.data?.error ||
                "Unable to find a profile for that email address."
            );
            setLoadingForm(false);
          }
        }
        return;
      }

      if (!isEditing) {
        if (savedDraft?.draftKey === draftKey) {
          await hydrateForm({
            ...createDefaultForm(),
            ...savedDraft.form
          });
          if (!ignore) {
            setLoadingForm(false);
          }
          return;
        }

        if (!ignore) {
          setLoadingForm(false);
        }
        return;
      }

      const activeEditSession = readActiveEditSession();

      if (!activeEditSession?.token) {
        if (!ignore) {
          clearPreviewMapDraft(draftKey);
          setError(
            "Your edit session has ended. Please return to the map and unlock this profile again."
          );
          setLoadingForm(false);
        }
        return;
      }

      const hydrateEditableCaregiver = async (caregiver) => {
        if (!caregiver || ignore) return;

        const draftForm =
          savedDraft?.draftKey === draftKey ? savedDraft.form : null;

        await hydrateForm({
          ...createDefaultForm(),
          ...createPreviewMapFormFromCaregiver(caregiver),
          ...draftForm
        });

        if (!ignore) {
          setHasPassword(Boolean(caregiver.hasPassword));
          setLoadingForm(false);
        }
      };

      try {
        const response = await axios.get(
          `${config.API_BASE_URL}/caregiver-temp-map/${caregiverID}`,
          {
            headers: {
              "x-preview-edit-session": activeEditSession.token
            }
          }
        );
        const caregiver = response.data.caregiver;

        if (!caregiver || caregiver.caregiverID !== caregiverID) {
          setError("No editable profile was found for this session.");
          setLoadingForm(false);
          return;
        }

        await hydrateEditableCaregiver(caregiver);
      } catch (lookupError) {
        if (!ignore) {
          clearPreviewMapDraft(draftKey);
          if ([401, 403].includes(lookupError.response?.status)) {
            clearPreviewEditSession(caregiverID);
          }
          setError(
            lookupError.response?.data?.error ||
              "Unable to load the profile for this edit session."
          );
          setLoadingForm(false);
        }
      }
    };

    loadInitialState();

    return () => {
      ignore = true;
    };
  }, [
    caregiverID,
    draftKey,
    isEditing,
    isLocationStep,
    loadCountryView,
    location.state,
    lookupEmail,
    navigate,
    readActiveEditSession,
    withLookupEmail
  ]);

  useEffect(() => {
    if (loadingForm) return;

    writePreviewMapDraft(draftKey, form);
  }, [draftKey, form, loadingForm]);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }

    const handlePopState = () => {
      if (!window.location.pathname.includes(editFlowPathFragment)) {
        clearPreviewMapDraft(draftKey);
      }
    };

    const handlePageHide = () => {
      clearPreviewMapDraft(draftKey);
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [draftKey, editFlowPathFragment, isEditing]);

  useEffect(() => {
    if (loadingForm || !isLocationStep || hasRequiredDetails) return;

    navigate(detailsPath, {
      replace: true,
      state: {
        errorMessage: "Complete step 1 first, then continue to the map step."
      }
    });
  }, [detailsPath, hasRequiredDetails, isLocationStep, loadingForm, navigate]);

  const handleTextChange = (field) => (event) => {
    setForm((previous) => ({
      ...previous,
      [field]: event.target.value
    }));
    setError("");
  };

  const handleDigitsChange = (field, maxLength = 3) => (event) => {
    setForm((previous) => ({
      ...previous,
      [field]: event.target.value.replace(/\D/g, "").slice(0, maxLength)
    }));
    setError("");
  };

  const handleExperienceChange = (index, field) => (event) => {
    const value = event.target.value;
    setForm((previous) => ({
      ...previous,
      experienceEntries: previous.experienceEntries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      )
    }));
    setError("");
  };

  const addExperienceEntry = () => {
    setForm((previous) => ({
      ...previous,
      experienceEntries: [...previous.experienceEntries, createEmptyExperience()].slice(0, 6)
    }));
    setError("");
  };

  const removeExperienceEntry = (index) => {
    setForm((previous) => {
      const nextEntries = previous.experienceEntries.filter(
        (_, entryIndex) => entryIndex !== index
      );

      return {
        ...previous,
        experienceEntries: nextEntries.length ? nextEntries : [createEmptyExperience()]
      };
    });
    setError("");
  };

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;

    setForm((previous) => ({
      ...previous,
      country: nextCountry,
      phone: sanitizePreviewNationalPhone(previous.phone, nextCountry)
    }));
    setError("");
  };

  const handlePhoneChange = (event) => {
    setForm((previous) => ({
      ...previous,
      phone: sanitizePreviewNationalPhone(event.target.value, previous.country)
    }));
    setError("");
  };

  const handlePhotoSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotoError("");
    setError("");

    if (!file.type.startsWith("image/")) {
      setPhotoError("Please choose an image file.");
      return;
    }

    try {
      const original = await readFileAsDataUrl(file);
      let processed = await compressImage(original);

      if (processed.length > MAX_PHOTO_SIZE) {
        processed = await compressImage(original, 420, 0.7);
      }

      if (processed.length > MAX_PHOTO_SIZE) {
        setPhotoError(
          "This image is still too large. Please choose a smaller photo."
        );
        return;
      }

      setForm((previous) => ({ ...previous, profilePhoto: processed }));
    } catch (processingError) {
      setPhotoError(processingError.message || "Unable to process that image.");
    } finally {
      event.target.value = "";
    }
  };

  const clearPhoto = () => {
    setForm((previous) => ({ ...previous, profilePhoto: "" }));
    setPhotoError("");
  };

  const validateBeforeStepTwo = () => {
    const missingFields = [];

    if (!hasFormValue(form.firstName)) missingFields.push("first name");
    if (!hasFormValue(form.lastName)) missingFields.push("last name");
    if (!hasFormValue(form.age)) missingFields.push("age");
    if (!hasFormValue(form.paymentMode)) missingFields.push("payment mode");
    if (!form.rate) missingFields.push("hourly rate");
    if (!hasFormValue(form.gender)) missingFields.push("gender");
    if (!hasFormValue(form.spokenLanguage)) missingFields.push("spoken language");
    if (!hasFormValue(form.city)) missingFields.push("city or town");
    if (!hasFormValue(form.phone)) missingFields.push("phone number");
    if (!normalizePreviewEmail(form.email)) missingFields.push("email");
    if (!hasFormValue(form.profilePhoto)) missingFields.push("profile photo");
    if (!hasFormValue(form.role)) missingFields.push("role");
    if (!hasFormValue(form.country)) missingFields.push("country");
    if (!hasFormValue(form.introVideoUrl)) missingFields.push("YouTube introduction video");

    if (missingFields.length) {
      setError(`Complete required fields: ${missingFields.join(", ")}.`);
      return false;
    }

    if (!rateOptionValues.has(String(form.rate))) {
      setError("Hourly rate must be between 1 and 6.");
      return false;
    }

    if (!isValidPreviewEmail(form.email)) {
      setError("Enter a valid email address.");
      return false;
    }

    if (!isValidPreviewYouTubeUrl(form.introVideoUrl)) {
      setError("Add a valid YouTube watch, Shorts, or youtu.be link for your introduction video.");
      return false;
    }

    if (Number(form.age) < 18 || Number(form.age) > 120) {
      setError("Age must be between 18 and 120.");
      return false;
    }

    if (!isConfirmedPreviewCountry(form.country) || !selectedCountryConfig) {
      setError("Choose a supported country from the list before continuing.");
      return false;
    }

    if (!isValidPreviewPhoneForCountry(form.country, form.phone)) {
      setError(`Enter ${selectedCountryConfig.digits} digits for ${selectedCountryConfig.name}.`);
      return false;
    }

    if (photoError) {
      setError(photoError);
      return false;
    }

    return true;
  };

  const handleContinueToLocation = async (event) => {
    event.preventDefault();

    if (!validateBeforeStepTwo()) {
      return;
    }

    writePreviewMapDraft(draftKey, form);
    navigate(locationPath);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateBeforeStepTwo()) {
      navigate(detailsPath);
      return;
    }

    if (photoError) {
      setError(photoError);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      if (password || confirmPassword) {
        if (password.length < 8) {
          setError("Password must be at least 8 characters.");
          setSubmitting(false);
          return;
        }

        if (password !== confirmPassword) {
          setError("Password and confirmation must match.");
          setSubmitting(false);
          return;
        }
      }

      const normalizedEmail = normalizePreviewEmail(form.email);
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        country: form.country,
        role: "caregiver",
        phone: formattedPhone,
        email: normalizedEmail,
        profilePhoto: form.profilePhoto,
        spokenLanguage: form.spokenLanguage,
        gender: form.gender,
        age: form.age,
        city: form.city,
        paymentMode: form.paymentMode,
        paymentMethod: form.paymentMode,
        rate: form.rate,
        status: form.status,
        professionalTitle: form.professionalTitle,
        bio: form.bio,
        introVideoUrl: form.introVideoUrl,
        yearsExperience: form.yearsExperience,
        specialties: form.specialties,
        certifications: form.certifications,
        availabilityNotes: form.availabilityNotes,
        experienceEntries: form.experienceEntries.filter((entry) =>
          Object.values(entry).some((value) => String(value || "").trim())
        ),
        latitude: null,
        longitude: null
      };

      if (password) {
        payload.password = password;
      }

      if (isEditing) {
        const activeEditSession = readActiveEditSession();

        if (!activeEditSession?.token) {
          clearPreviewMapDraft(draftKey);
          setError(
            "Your edit session has ended. Please return to the map and unlock this profile again."
          );
          setSubmitting(false);
          return;
        }

        const response = await axios.put(
          `${config.API_BASE_URL}/caregiver-temp-map/${caregiverID}`,
          payload,
          {
            headers: {
              "x-preview-edit-session": activeEditSession.token
            }
          }
        );

        clearPreviewMapDraft(draftKey);
        setPassword("");
        setConfirmPassword("");
        navigate("/realMap", {
          state: {
            successMessage: "Caregiver updated.",
            focusCaregiverID: response.data.caregiver?.caregiverID || caregiverID
          }
        });

        return;
      }

      const response = await axios.post(
        `${config.API_BASE_URL}/caregiver-temp-map`,
        payload,
        {
          headers: {
            "x-preview-owner-key": getPreviewOwnerKey()
          }
        }
      );
      const nextCaregiver = response.data.caregiver;
      const deleteToken = response.data.deleteToken;
      const ownedTokens = readOwnedCaregiverTokens();

      if (nextCaregiver?.caregiverID && deleteToken) {
        writeOwnedCaregiverTokens({
          ...ownedTokens,
          [nextCaregiver.caregiverID]: deleteToken
        });
      }

      clearPreviewMapDraft(draftKey);
      navigate("/realMap", {
        state: {
          successMessage: "Caregiver added.",
          focusCaregiverID: nextCaregiver?.caregiverID || ""
        }
      });
    } catch (submitError) {
      setError(
        submitError.response?.data?.error ||
          `Unable to ${isEditing ? "update" : "add"} caregiver.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const secondaryAction = isLocationStep
    ? {
        label: isEditing ? "Save profile changes" : "Save profile",
        formId: "preview-map-location-form"
      }
    : { label: "Next: Review profile", formId: "preview-map-details-form" };

  const handleBack = () => {
    if (isEditing && !isLocationStep) {
      clearPreviewMapDraft(draftKey);
      navigate("/realMap", {
        replace: true,
        state: { focusCaregiverID: caregiverID }
      });
      return;
    }

    navigate(isLocationStep ? detailsPath : "/realMap");
  };

  const handleCaregiverLogout = async () => {
    clearPreviewMapDraft(draftKey);
    await revokeEditSession();
    navigate("/realMap", {
      replace: true,
      state: {
        successMessage: "Caregiver logged out."
      }
    });
  };

  return (
    <div style={page} className="preview-map-form-page">
      <style>{responsiveStyles}</style>

      <div style={shell}>
        <div style={topBar} className="preview-form-top-bar">
          <button
            type="button"
            style={backButton}
            onClick={handleBack}
          >
            <FaArrowLeft />
            {isLocationStep ? "Back to details" : "Back to my profile"}
          </button>

          <div style={topBarActions} className="preview-form-top-actions">
            {isEditing && (
              <button
                type="button"
                style={logoutButton}
                onClick={handleCaregiverLogout}
                disabled={submitting}
              >
                <FaSignOutAlt />
                Logout
              </button>
            )}

            <button
              type="submit"
              form={secondaryAction.formId}
              style={submitButton}
              className="preview-form-top-submit"
              disabled={submitting || resolvingCountry}
            >
              {submitting
                ? "Saving..."
                : secondaryAction.label}
              {!isLocationStep && !submitting && <FaArrowRight aria-hidden="true" />}
            </button>
          </div>
        </div>

        {(error || photoError) && (
          <section ref={feedbackRef} style={bannerRow} role="alert">
            {error && <div style={errorBanner}>{error}</div>}
            {photoError && <div style={errorBanner}>{photoError}</div>}
          </section>
        )}

        <section style={stepCard}>
          <div style={stepBadge}>
            {isLocationStep ? "Step 2 of 2" : "Step 1 of 2"}
          </div>
          <h1 style={stepTitle}>
            {isLocationStep
              ? "Review your profile preview"
              : "Enter your caregiver details"}
          </h1>
          <p style={stepText}>
            {isLocationStep
              ? `Your profile will be grouped automatically under ${form.country || "the selected country"}. Review the preview below, then save when everything looks right.`
              : "Fill in the required details, use the profile email that identifies you on the map, and upload your photo first. Imported profiles can start here with their details already prefilled from the CSV."}
          </p>
        </section>

        {!loadingForm && (
          <section style={completionCard} aria-live="polite">
            <div style={completionHeader}>
              <div>
                <div style={completionEyebrow}>Profile readiness</div>
                <strong style={completionTitle}>
                  {profileCompletion.profileComplete
                    ? "Ready for the elder map"
                    : `${profileCompletion.missingProfileFields.length} required item${profileCompletion.missingProfileFields.length === 1 ? "" : "s"} remaining`}
                </strong>
              </div>
              <div style={completionPercent}>{profileCompletion.profileCompletionPercent}%</div>
            </div>
            <div
              style={completionTrack}
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={profileCompletion.profileCompletionPercent}
            >
              <span
                style={{
                  ...completionBar,
                  width: `${profileCompletion.profileCompletionPercent}%`
                }}
              />
            </div>
            {profileCompletion.profileComplete ? (
              <p style={completionMessage}>
                Everything families need is present. Saving publishes these changes to the elder map.
              </p>
            ) : (
              <>
                <p style={completionMessage}>
                  Complete every required item to publish your profile. Your YouTube introduction is mandatory.
                </p>
                <div style={completionChips}>
                  {profileCompletion.missingProfileFields.slice(0, 6).map((field) => (
                    <span key={field.key} style={completionChip}>{field.label}</span>
                  ))}
                  {profileCompletion.missingProfileFields.length > 6 && (
                    <span style={completionChip}>
                      +{profileCompletion.missingProfileFields.length - 6} more
                    </span>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {loadingForm ? (
          <div style={loadingCard}>Loading...</div>
        ) : isLocationStep ? (
          <form
            id="preview-map-location-form"
            style={formCard}
            onSubmit={handleSubmit}
          >
            <section style={summaryCard} className="preview-summary-card">
              {form.profilePhoto ? (
                <img
                  src={form.profilePhoto}
                  alt={`${form.firstName} ${form.lastName}`}
                  style={summaryPhoto}
                />
              ) : (
                <div style={summaryFallback}>
                  {`${form.firstName?.[0] || ""}${form.lastName?.[0] || ""}`.toUpperCase() ||
                    "CG"}
                </div>
              )}

              <div style={summaryBody}>
                <div style={summaryName}>
                  {form.firstName} {form.lastName}
                </div>
                <div style={summaryCountry}>{form.country}</div>
                {(form.role || form.phone) && (
                  <div style={summaryMetaRow}>
                    {form.role && (
                      <span style={summaryRoleBadge}>
                        {formatPreviewRole(form.role)}
                      </span>
                    )}
                    {formattedPhone && (
                      <span style={summaryMetaText}>{formattedPhone}</span>
                    )}
                  </div>
                )}
                <div style={summaryHint}>
                  Your preview is grouped automatically by country. If you need
                  to change your details, photo, or country, go back to step 1
                  and update them there.
                </div>
              </div>
            </section>

            <section style={previewFactsGrid} className="preview-facts-grid">
              <div style={previewFactCard}>
                <div style={previewFactLabel}>Email</div>
                <div style={previewFactValue}>{form.email || "Not provided"}</div>
              </div>

              <div style={previewFactCard}>
                <div style={previewFactLabel}>Map grouping</div>
                <div style={previewFactValue}>{form.country || "Country not set"}</div>
              </div>

              <div style={previewFactCard}>
                <div style={previewFactLabel}>Contact</div>
                <div style={previewFactValue}>{formattedPhone || "No phone added yet"}</div>
              </div>

              <div style={previewFactCard}>
                <div style={previewFactLabel}>Language</div>
                <div style={previewFactValue}>{form.spokenLanguage || "Not listed"}</div>
              </div>

              <div style={previewFactCard}>
                <div style={previewFactLabel}>Rate</div>
                <div style={previewFactValue}>{form.rate ? `$${form.rate}/hr` : "Not listed"}</div>
              </div>

              <div style={previewFactCard}>
                <div style={previewFactLabel}>Availability</div>
                <div style={previewFactValue}>
                  {form.status === "unavailable" ? "Unavailable" : "Available"}
                </div>
              </div>
            </section>

            <div style={helperCard}>
              {resolvingCountry
                ? "Preparing the automatic country grouping..."
                : `This page is only a preview. We will group this profile under ${form.country} automatically, so there is nothing else to position manually.`}
            </div>
          </form>
        ) : (
          <form
            id="preview-map-details-form"
            style={formCard}
            onSubmit={handleContinueToLocation}
          >
            <section style={requiredDetailsCard} aria-labelledby="required-details-title">
              <div style={requiredHeader}>
                <div>
                  <div style={requiredEyebrow}>Required before publishing</div>
                  <h2 id="required-details-title" style={sectionTitle}>Mandatory caregiver information</h2>
                  <p style={sectionText}>
                    Complete every field in this section. Fields marked <span style={requiredMarker}>*</span> are mandatory and your profile cannot appear on the elder map until all are valid.
                  </p>
                </div>
                <span style={requiredCountBadge}>
                  {profileCompletion.missingProfileFields.length
                    ? `${profileCompletion.missingProfileFields.length} remaining`
                    : "All complete"}
                </span>
              </div>

              <div style={requiredMediaGrid} className="preview-required-media-grid">
                <div style={requiredMediaPanel}>
                  <div style={photoHeader}>
                    <div>
                      <h3 style={requiredMediaTitle}>Profile photo <span style={requiredMarker}>*</span></h3>
                      <p style={sectionText}>Upload a clear, recent photo that families can recognize.</p>
                    </div>
                    <div style={photoActions}>
                      <label style={uploadButton}>
                        <FaCloudUploadAlt />
                        {form.profilePhoto ? "Change photo" : "Upload photo"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handlePhotoSelected}
                          style={{ display: "none" }}
                        />
                      </label>
                      {form.profilePhoto && (
                        <button type="button" style={clearPhotoButton} onClick={clearPhoto}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={requiredPhotoPreviewWrap}>
                    {form.profilePhoto ? (
                      <img
                        src={form.profilePhoto}
                        alt={`${form.firstName || "Caregiver"} profile preview`}
                        style={requiredPhotoPreview}
                      />
                    ) : (
                      <div style={photoPlaceholder}>Your required profile photo preview will appear here.</div>
                    )}
                  </div>
                </div>

                <div style={requiredMediaPanel}>
                  <Field label="YouTube introduction link" required>
                    <input
                      style={input}
                      type="url"
                      value={form.introVideoUrl}
                      onChange={handleTextChange("introVideoUrl")}
                      placeholder="https://www.youtube.com/watch?v=..."
                      aria-describedby="youtube-preview-help"
                      required
                    />
                    <div id="youtube-preview-help" style={helperText}>
                      Paste your link and watch the preview below before saving. You can change it until the correct video appears.
                    </div>
                  </Field>
                  <div style={videoPreviewWrap}>
                    {videoPreviewUrl ? (
                      <iframe
                        src={videoPreviewUrl}
                        title="YouTube introduction preview"
                        style={videoPreviewFrame}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div style={videoPlaceholder}>
                        {form.introVideoUrl
                          ? "This link cannot be previewed. Use a valid YouTube watch, Shorts, embed, or youtu.be link."
                          : "Your YouTube video preview will appear here before it is published."}
                      </div>
                    )}
                  </div>
                  {videoPreviewUrl && (
                    <a
                      href={form.introVideoUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={videoExternalLink}
                    >
                      Open this video on YouTube ↗
                    </a>
                  )}
                </div>
              </div>

              <div style={requiredFieldsGrid} className="preview-details-grid">
              <Field label="First name" required>
                <input
                  style={input}
                  value={form.firstName}
                  onChange={handleTextChange("firstName")}
                  placeholder="First name"
                  required
                />
              </Field>

              <Field label="Last name" required>
                <input
                  style={input}
                  value={form.lastName}
                  onChange={handleTextChange("lastName")}
                  placeholder="Last name"
                  required
                />
              </Field>

              <Field label="Country" required>
                <select
                  style={input}
                  value={form.country}
                  onChange={handleCountryChange}
                  required
                >
                  <option value="">Select country</option>
                  {PREVIEW_COUNTRY_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.displayLabel || option.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>

              <Field label="Role" required>
                <input style={readOnlyInput} value="Caregiver" readOnly aria-readonly="true" />
                <div style={helperText}>Your role is fixed securely and cannot be changed.</div>
              </Field>

              <Field label="Phone / WhatsApp" required>
                <div style={phoneRow} className="preview-phone-row">
                  <input
                    style={codeInput}
                    value={selectedCountryConfig?.dialCode || ""}
                    readOnly
                    disabled
                    placeholder="+Code"
                    aria-label="Country code"
                  />
                  <input
                    style={phoneInput}
                    value={form.phone}
                    onChange={handlePhoneChange}
                    placeholder={
                      selectedCountryConfig
                        ? `${selectedCountryConfig.digits} digits`
                        : "Select country first"
                    }
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={selectedCountryConfig?.digits || 16}
                    disabled={!selectedCountryConfig}
                    aria-label="Phone number"
                    required
                  />
                </div>
                <div style={helperText}>{getPreviewPhoneGuidance(form.country)}</div>
              </Field>

              <Field label="Email address" required>
                {isEditing ? (
                  <input
                    style={readOnlyInput}
                    value={form.email}
                    readOnly
                    aria-readonly="true"
                  />
                ) : (
                  <input
                    style={input}
                    type="email"
                    value={form.email}
                    onChange={handleTextChange("email")}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                )}
              </Field>

              <Field label="Spoken language" required>
                <select
                  style={input}
                  value={form.spokenLanguage}
                  onChange={handleTextChange("spokenLanguage")}
                  required
                >
                  {languageOptions.map((language) => (
                    <option key={language || "blank"} value={language}>
                      {language || "Select language"}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Gender" required>
                <select
                  style={input}
                  value={form.gender}
                  onChange={handleTextChange("gender")}
                  required
                >
                  {genderOptions.map((option) => (
                    <option key={option.value || "blank"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Age" required>
                <input
                  style={input}
                  type="number"
                  value={form.age}
                  onChange={handleDigitsChange("age", 3)}
                  placeholder="Age"
                  inputMode="numeric"
                  min="18"
                  max="120"
                  required
                />
              </Field>

              <Field label="City or town" required>
                <input
                  style={input}
                  value={form.city}
                  onChange={handleTextChange("city")}
                  placeholder="City"
                  required
                />
              </Field>

              <Field label="Payment mode" required>
                <select
                  style={input}
                  value={form.paymentMode}
                  onChange={handleTextChange("paymentMode")}
                  required
                >
                  {paymentOptions.map((option) => (
                    <option key={option.value || "blank"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Hourly rate" required>
                <select
                  style={input}
                  value={form.rate}
                  onChange={handleTextChange("rate")}
                  required
                >
                  <option value="">Select rate</option>
                  {rateOptions.map((rate) => (
                    <option key={rate} value={rate}>
                      ${rate}/hr
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Availability" required>
                <select
                  style={input}
                  value={form.status}
                  onChange={handleTextChange("status")}
                  required
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              </div>
            </section>

            <section style={profileDetailsCard}>
              <div style={photoHeader}>
                <div>
                  <h2 style={sectionTitle}>Optional profile details</h2>
                  <p style={sectionText}>
                    Add helpful background information for families. These fields are optional and do not block publication.
                  </p>
                </div>
              </div>

              {isEditing && (
                <div style={detailsGrid} className="preview-details-grid">
                  <Field label={hasPassword ? "Change password" : "Create password"}>
                    <input
                      style={input}
                      type={showPasswords ? "text" : "password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder={
                        hasPassword
                          ? "Leave blank to keep current password"
                          : "At least 8 characters"
                      }
                      autoComplete="new-password"
                    />
                  </Field>

                  <Field label="Confirm password">
                    <input
                      style={input}
                      type={showPasswords ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      placeholder="Retype password"
                      autoComplete="new-password"
                    />
                  </Field>

                  <button type="button" style={passwordVisibilityButton} onClick={() => setShowPasswords((shown) => !shown)}>
                    {showPasswords ? <FaEyeSlash /> : <FaEye />}
                    {showPasswords ? "Hide passwords" : "Show passwords"}
                  </button>

                  <div style={securityNote}>
                    {hasPassword
                      ? "Your password protects this profile together with your email."
                      : "Create a password so future edits require both your email and password."}
                  </div>
                </div>
              )}

              <div style={detailsGrid} className="preview-details-grid">
                <Field label="Professional title">
                  <input
                    style={input}
                    value={form.professionalTitle}
                    onChange={handleTextChange("professionalTitle")}
                    placeholder="Example: Senior home caregiver"
                  />
                </Field>

                <Field label="Years experience">
                  <input
                    style={input}
                    value={form.yearsExperience}
                    onChange={handleDigitsChange("yearsExperience", 2)}
                    placeholder="Years"
                    inputMode="numeric"
                  />
                </Field>

                <Field label="Specialties">
                  <input
                    style={input}
                    value={form.specialties}
                    onChange={handleTextChange("specialties")}
                    placeholder="Dementia care, mobility support"
                  />
                </Field>
              </div>

              <div style={detailsGrid} className="preview-details-grid">
                <Field label="Certifications / training">
                  <textarea
                    style={textarea}
                    value={form.certifications}
                    onChange={handleTextChange("certifications")}
                    placeholder="Training, certificates, or credentials"
                  />
                </Field>

                <Field label="Availability notes">
                  <textarea
                    style={textarea}
                    value={form.availabilityNotes}
                    onChange={handleTextChange("availabilityNotes")}
                    placeholder="Days, hours, or care availability"
                  />
                </Field>

                <Field label="Short bio">
                  <textarea
                    style={textarea}
                    value={form.bio}
                    onChange={handleTextChange("bio")}
                    placeholder="Tell families about yourself"
                  />
                </Field>

              </div>

              <div style={experienceHeader}>
                <h3 style={sectionTitle}>Work history</h3>
                <button
                  type="button"
                  style={smallSecondaryButton}
                  onClick={addExperienceEntry}
                  disabled={form.experienceEntries.length >= 6}
                >
                  Add experience
                </button>
              </div>

              {form.experienceEntries.map((entry, index) => (
                <div key={`experience-${index}`} style={experienceCard}>
                  <div style={experienceHeader}>
                    <strong>Experience {index + 1}</strong>
                    {form.experienceEntries.length > 1 && (
                      <button
                        type="button"
                        style={clearPhotoButton}
                        onClick={() => removeExperienceEntry(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={detailsGrid} className="preview-details-grid">
                    <Field label="Employer">
                      <input
                        style={input}
                        value={entry.employer}
                        onChange={handleExperienceChange(index, "employer")}
                        placeholder="Employer"
                      />
                    </Field>

                    <Field label="Role">
                      <input
                        style={input}
                        value={entry.role}
                        onChange={handleExperienceChange(index, "role")}
                        placeholder="Caregiver role"
                      />
                    </Field>

                    <Field label="Location / period">
                      <input
                        style={input}
                        value={entry.period}
                        onChange={handleExperienceChange(index, "period")}
                        placeholder="Example: 2024-2026"
                      />
                    </Field>
                  </div>

                  <textarea
                    style={textarea}
                    value={entry.notes}
                    onChange={handleExperienceChange(index, "notes")}
                    placeholder="Brief notes about this experience"
                  />
                </div>
              ))}
            </section>

          </form>
        )}

        {!loadingForm && (
          <div className="preview-form-mobile-actions" aria-label="Profile form actions">
            <div className="preview-form-mobile-step">
              <span>{isLocationStep ? "Final step" : "Step 1 of 2"}</span>
              <strong>{isLocationStep ? "Review and save" : "Continue when ready"}</strong>
            </div>
            <button
              type="submit"
              form={secondaryAction.formId}
              disabled={submitting || resolvingCountry}
            >
              <span>{submitting ? "Saving..." : secondaryAction.label}</span>
              {!isLocationStep && !submitting && <FaArrowRight aria-hidden="true" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children, required = false }) => (
  <div style={field}>
    <label style={labelStyle}>
      {label}
      {required ? <span style={requiredMarker}>*</span> : null}
    </label>
    {children}
  </div>
);

export default PreviewMapForm;

const responsiveStyles = `
  .preview-form-mobile-actions {
    display: none;
  }

  .preview-details-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .preview-required-media-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 980px) {
    .preview-form-top-bar,
    .preview-form-top-actions {
      width: 100% !important;
    }

    .preview-form-top-bar {
      display: grid !important;
      grid-template-columns: 1fr !important;
    }

    .preview-details-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }

  @media (max-width: 760px) {
    .preview-map-form-page {
      padding-bottom: 104px !important;
    }

    .preview-form-top-submit {
      display: none !important;
    }

    .preview-form-mobile-actions {
      position: fixed;
      left: 50%;
      bottom: max(8px, env(safe-area-inset-bottom));
      z-index: 1200;
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 10px;
      width: min(calc(100% - 16px), 560px);
      padding: 9px;
      border: 1px solid rgba(255, 255, 255, 0.72);
      border-radius: 20px;
      background: rgba(255, 255, 255, 0.96);
      box-shadow: 0 18px 46px rgba(12, 42, 68, 0.28);
      backdrop-filter: blur(16px);
      transform: translateX(-50%);
    }

    .preview-form-mobile-step {
      display: grid;
      gap: 2px;
      min-width: 0;
      padding-left: 5px;
      color: #14324c;
    }

    .preview-form-mobile-step span {
      color: #58718a;
      font-size: 0.74rem;
      font-weight: 800;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .preview-form-mobile-step strong {
      overflow: hidden;
      font-size: 0.88rem;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .preview-form-mobile-actions > button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 50px;
      max-width: 58vw;
      padding: 0 18px;
      border: 0;
      border-radius: 15px;
      background: linear-gradient(135deg, #0e5580, #156893);
      color: #ffffff;
      font-size: 0.94rem;
      font-weight: 900;
      box-shadow: 0 10px 22px rgba(14, 85, 128, 0.24);
    }

    .preview-form-mobile-actions > button:disabled {
      cursor: wait;
      opacity: 0.62;
    }

    .preview-form-top-actions {
      display: grid !important;
      grid-template-columns: 1fr !important;
    }

    .preview-form-top-bar button,
    .preview-form-top-actions button {
      width: 100% !important;
      justify-content: center !important;
      min-height: 48px !important;
    }

    .preview-details-grid {
      grid-template-columns: 1fr !important;
      padding: 14px !important;
      border-radius: 18px !important;
    }

    .preview-required-media-grid {
      grid-template-columns: 1fr !important;
    }

    .preview-summary-card {
      grid-template-columns: 1fr !important;
      padding: 14px !important;
      border-radius: 18px !important;
    }

    .preview-facts-grid {
      grid-template-columns: 1fr !important;
    }

    .preview-phone-row {
      grid-template-columns: 1fr !important;
    }

    .preview-summary-card img,
    .preview-summary-card > div:first-child {
      width: 112px !important;
      height: 112px !important;
      border-radius: 20px !important;
    }
  }

  @media (max-width: 420px) {
    .preview-form-mobile-actions {
      grid-template-columns: 1fr;
    }

    .preview-form-mobile-step {
      display: none;
    }

    .preview-form-mobile-actions > button {
      width: 100%;
      max-width: none;
    }

    .preview-details-grid,
    .preview-summary-card,
    .preview-facts-grid {
      gap: 12px !important;
    }
  }
`;

const page = {
  minHeight: "100vh",
  padding: "18px 12px 20px",
  background:
    "radial-gradient(circle at top left, rgba(255, 207, 84, 0.12), transparent 24%), linear-gradient(180deg, #eef5f8 0%, #dfe9f2 100%)"
};

const shell = {
  maxWidth: "1240px",
  margin: "0 auto",
  display: "grid",
  gap: "14px"
};

const topBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap"
};

const topBarActions = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
  flexWrap: "wrap"
};

const backButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  minHeight: "42px",
  padding: "0 16px",
  borderRadius: "999px",
  border: "1px solid rgba(14, 85, 128, 0.14)",
  background: "#ffffff",
  color: "#123a59",
  cursor: "pointer",
  fontWeight: "700"
};

const logoutButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  minHeight: "44px",
  padding: "0 16px",
  borderRadius: "999px",
  border: "1px solid rgba(180, 35, 24, 0.16)",
  background: "#fff5f4",
  color: "#b42318",
  cursor: "pointer",
  fontWeight: "800"
};

const submitButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "44px",
  padding: "0 20px",
  border: "none",
  borderRadius: "999px",
  background: "linear-gradient(135deg, #0e5580, #156893)",
  color: "#ffffff",
  fontWeight: "800",
  cursor: "pointer"
};

const bannerRow = {
  display: "grid",
  gap: "10px"
};

const errorBanner = {
  padding: "12px 14px",
  borderRadius: "16px",
  background: "#fff1f1",
  border: "1px solid #f3c2be",
  color: "#b42318",
  fontSize: "14px"
};

const stepCard = {
  display: "grid",
  gap: "10px",
  padding: "20px 22px",
  borderRadius: "28px",
  background:
    "linear-gradient(135deg, rgba(13, 84, 128, 0.95), rgba(24, 111, 149, 0.9))",
  color: "#ffffff",
  boxShadow: "0 28px 60px rgba(13, 44, 68, 0.2)"
};

const stepBadge = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "fit-content",
  minHeight: "34px",
  padding: "0 14px",
  borderRadius: "999px",
  background: "rgba(255, 255, 255, 0.18)",
  fontSize: "13px",
  fontWeight: "800",
  letterSpacing: "0.04em",
  textTransform: "uppercase"
};

const stepTitle = {
  margin: 0,
  fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
  lineHeight: 1.1
};

const stepText = {
  margin: 0,
  maxWidth: "760px",
  color: "rgba(255, 255, 255, 0.9)",
  fontSize: "15px",
  lineHeight: 1.65
};

const formCard = {
  display: "grid",
  gap: "14px"
};

const detailsGrid = {
  display: "grid",
  gap: "14px",
  padding: "16px",
  borderRadius: "24px",
  background: "rgba(248, 252, 255, 0.98)",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  boxShadow: "0 16px 36px rgba(12, 42, 68, 0.1)"
};

const field = {
  display: "grid",
  gap: "8px"
};

const labelStyle = {
  color: "#14324c",
  fontSize: "14px",
  fontWeight: "800"
};

const requiredMarker = {
  color: "#b42318",
  marginLeft: "4px"
};

const input = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "16px",
  border: "1px solid #c8d9e8",
  background: "#f8fbfe",
  color: "#11233c",
  fontSize: "15px",
  boxSizing: "border-box"
};

const textarea = {
  ...input,
  minHeight: "110px",
  resize: "vertical",
  lineHeight: 1.55,
  fontFamily: "inherit"
};

const readOnlyInput = {
  ...input,
  background: "#eef5fb",
  color: "#486581",
  cursor: "not-allowed"
};

const phoneRow = {
  display: "grid",
  gridTemplateColumns: "110px minmax(0, 1fr)",
  gap: "10px",
  alignItems: "center"
};

const codeInput = {
  ...input,
  background: "#eef5fb",
  color: "#486581",
  fontWeight: "700",
  cursor: "not-allowed"
};

const phoneInput = {
  ...input
};

const helperText = {
  marginTop: "8px",
  color: "#627d98",
  fontSize: "13px",
  lineHeight: 1.5
};

const requiredDetailsCard = {
  display: "grid",
  gap: "18px",
  padding: "clamp(16px, 2.4vw, 24px)",
  borderRadius: "28px",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbfe 100%)",
  border: "2px solid rgba(14, 85, 128, 0.16)",
  boxShadow: "0 20px 48px rgba(12, 42, 68, 0.12)"
};

const requiredHeader = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap"
};

const requiredEyebrow = {
  marginBottom: "7px",
  color: "#b42318",
  fontSize: "12px",
  fontWeight: 900,
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const requiredCountBadge = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "34px",
  padding: "0 13px",
  borderRadius: "999px",
  background: "#fff3d6",
  color: "#7a5100",
  fontSize: "13px",
  fontWeight: 900
};

const requiredMediaGrid = {
  display: "grid",
  gap: "16px"
};

const requiredMediaPanel = {
  display: "grid",
  alignContent: "start",
  gap: "14px",
  padding: "16px",
  borderRadius: "22px",
  background: "#f5f9fc",
  border: "1px solid rgba(14, 85, 128, 0.11)"
};

const requiredMediaTitle = {
  margin: 0,
  color: "#102a43",
  fontSize: "15px",
  fontWeight: 800
};

const requiredFieldsGrid = {
  ...detailsGrid,
  padding: "16px",
  background: "#f5f9fc",
  boxShadow: "none"
};

const requiredPhotoPreviewWrap = {
  display: "grid",
  placeItems: "center",
  minHeight: "230px",
  borderRadius: "18px",
  background: "#ffffff",
  border: "1px dashed rgba(14, 85, 128, 0.24)",
  overflow: "hidden"
};

const requiredPhotoPreview = {
  width: "100%",
  height: "230px",
  objectFit: "cover"
};

const videoPreviewWrap = {
  position: "relative",
  display: "grid",
  placeItems: "center",
  minHeight: "230px",
  borderRadius: "18px",
  background: "#102a43",
  overflow: "hidden"
};

const videoPreviewFrame = {
  width: "100%",
  aspectRatio: "16 / 9",
  minHeight: "230px",
  border: 0
};

const videoPlaceholder = {
  padding: "24px",
  color: "#d9e7f2",
  textAlign: "center",
  fontSize: "14px",
  lineHeight: 1.65
};

const videoExternalLink = {
  width: "fit-content",
  color: "#0e5580",
  fontSize: "13px",
  fontWeight: 800,
  textDecoration: "none"
};

const photoCard = {
  display: "grid",
  gap: "16px",
  padding: "18px",
  borderRadius: "24px",
  background: "rgba(248, 252, 255, 0.98)",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  boxShadow: "0 16px 36px rgba(12, 42, 68, 0.1)"
};

const profileDetailsCard = {
  ...photoCard
};

const securityNote = {
  alignSelf: "end",
  color: "#52606d",
  fontSize: "14px",
  lineHeight: 1.5,
  padding: "12px 0"
};
const passwordVisibilityButton = {
  alignSelf: "center",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "44px",
  padding: "0 14px",
  borderRadius: "12px",
  border: "1px solid #cbd5e0",
  background: "#f8fafc",
  color: "#176f68",
  fontWeight: 800,
  cursor: "pointer"
};

const photoHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
  flexWrap: "wrap"
};

const sectionTitle = {
  margin: 0,
  color: "#102a43",
  fontSize: "18px"
};

const sectionText = {
  margin: "6px 0 0",
  color: "#486581",
  fontSize: "14px",
  lineHeight: 1.6
};

const photoActions = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center"
};

const uploadButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  minHeight: "46px",
  padding: "0 16px",
  borderRadius: "16px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.14)",
  color: "#123a59",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "14px"
};

const clearPhotoButton = {
  minHeight: "46px",
  padding: "0 16px",
  borderRadius: "16px",
  border: "1px solid rgba(70, 98, 123, 0.18)",
  background: "#ffffff",
  color: "#334e68",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "14px"
};

const smallSecondaryButton = {
  ...clearPhotoButton,
  minHeight: "40px"
};

const experienceHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const experienceCard = {
  display: "grid",
  gap: "12px",
  padding: "14px",
  borderRadius: "18px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.1)"
};

const photoPlaceholder = {
  padding: "24px",
  textAlign: "center",
  color: "#627d98",
  fontSize: "15px",
  lineHeight: 1.7
};

const summaryCard = {
  display: "grid",
  gridTemplateColumns: "140px minmax(0, 1fr)",
  gap: "18px",
  alignItems: "center",
  padding: "18px",
  borderRadius: "24px",
  background: "rgba(248, 252, 255, 0.98)",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  boxShadow: "0 16px 36px rgba(12, 42, 68, 0.1)"
};

const summaryPhoto = {
  width: "140px",
  height: "140px",
  objectFit: "cover",
  borderRadius: "24px"
};

const summaryFallback = {
  width: "140px",
  height: "140px",
  borderRadius: "24px",
  display: "grid",
  placeItems: "center",
  background: "linear-gradient(135deg, #f4d35e, #f7b267)",
  color: "#6b3d00",
  fontWeight: "800",
  fontSize: "36px"
};

const summaryBody = {
  display: "grid",
  gap: "8px"
};

const summaryName = {
  color: "#102a43",
  fontSize: "24px",
  fontWeight: "800"
};

const summaryCountry = {
  width: "fit-content",
  padding: "8px 12px",
  borderRadius: "999px",
  background: "#e8f1f8",
  color: "#0e5580",
  fontWeight: "700"
};

const summaryHint = {
  color: "#486581",
  fontSize: "14px",
  lineHeight: 1.65
};

const previewFactsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px"
};

const previewFactCard = {
  padding: "16px",
  borderRadius: "20px",
  background: "rgba(248, 252, 255, 0.98)",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  boxShadow: "0 16px 36px rgba(12, 42, 68, 0.08)",
  display: "grid",
  gap: "8px"
};

const previewFactLabel = {
  color: "#7b8794",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.04em",
  textTransform: "uppercase"
};

const previewFactValue = {
  color: "#102a43",
  fontSize: "16px",
  fontWeight: "700",
  lineHeight: 1.5,
  wordBreak: "break-word"
};

const summaryMetaRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center"
};

const summaryRoleBadge = {
  display: "inline-flex",
  alignItems: "center",
  minHeight: "32px",
  padding: "0 12px",
  borderRadius: "999px",
  background: "#fff3d6",
  color: "#8a5d00",
  fontWeight: "800",
  fontSize: "13px"
};

const summaryMetaText = {
  color: "#486581",
  fontSize: "14px",
  fontWeight: "700"
};

const helperCard = {
  padding: "14px 16px",
  borderRadius: "20px",
  background: "rgba(248, 252, 255, 0.98)",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  boxShadow: "0 16px 36px rgba(12, 42, 68, 0.08)",
  color: "#486581",
  fontSize: "14px",
  lineHeight: 1.65
};

const completionCard = {
  padding: "18px",
  borderRadius: "24px",
  background: "linear-gradient(135deg, #0d5f59, #167d68)",
  color: "#ffffff",
  boxShadow: "0 18px 42px rgba(13, 95, 89, 0.2)",
  display: "grid",
  gap: "12px"
};

const completionHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px"
};

const completionEyebrow = {
  color: "#c9f2e8",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const completionTitle = {
  display: "block",
  marginTop: "4px",
  fontSize: "18px"
};

const completionPercent = {
  flex: "0 0 auto",
  fontSize: "24px",
  fontWeight: 900
};

const completionTrack = {
  height: "9px",
  borderRadius: "999px",
  overflow: "hidden",
  background: "rgba(255, 255, 255, 0.22)"
};

const completionBar = {
  display: "block",
  height: "100%",
  borderRadius: "inherit",
  background: "linear-gradient(90deg, #f4d35e, #ffe69a)",
  transition: "width 180ms ease"
};

const completionMessage = {
  margin: 0,
  color: "#e9fffa",
  lineHeight: 1.55,
  fontSize: "14px"
};

const completionChips = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
};

const completionChip = {
  padding: "7px 10px",
  borderRadius: "999px",
  background: "rgba(255, 255, 255, 0.14)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 700
};

const loadingCard = {
  padding: "20px",
  borderRadius: "24px",
  background: "rgba(248, 252, 255, 0.98)",
  border: "1px solid rgba(14, 85, 128, 0.08)",
  color: "#486581"
};
